import { z } from 'zod';
import { ErrorResponseSchema } from '../../common/base';

/**
 * Success response schema for user deactivation
 * Simple success without message
 */
export const DeactivateUserSuccessSchema = z.object({
  success: z.literal(true),
});

/**
 * Combined response schema for user deactivation endpoint
 * POST /api/user/deactivate
 *
 * Discriminated union based on success field
 *
 * @example
 * ```typescript
 * const response = DeactivateUserResponseSchema.parse(data);
 * if (response.success) {
 *   console.log('Account deactivated');
 * } else {
 *   console.error('Error:', response.error);
 * }
 * ```
 */
export const DeactivateUserResponseSchema = z.discriminatedUnion('success', [
  DeactivateUserSuccessSchema,
  ErrorResponseSchema,
]);

export type DeactivateUserSuccess = z.infer<typeof DeactivateUserSuccessSchema>;
export type DeactivateUserResponse = z.infer<typeof DeactivateUserResponseSchema>;

/**
 * POST /api/user/deactivate body: the code sent by POST /api/user/close-code.
 * Closing needs it; a signed-in session alone is not enough.
 */
export const DeactivateUserRequestSchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code we sent you.'),
});

export type DeactivateUserRequest = z.infer<typeof DeactivateUserRequestSchema>;

/** POST /api/user/close-code: where the code went, masked. */
export const CloseCodeSuccessSchema = z.object({
  success: z.literal(true),
  channel: z.enum(['email', 'phone']),
  sentTo: z.string(),
});

export const CloseCodeResponseSchema = z.discriminatedUnion('success', [
  CloseCodeSuccessSchema,
  ErrorResponseSchema,
]);

export type CloseCodeResponse = z.infer<typeof CloseCodeResponseSchema>;
